import type maplibregl from 'maplibre-gl';
import type { GeoJSONSource } from 'maplibre-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FlightTrack, TrackPoint } from '../lib/api';
import { altitudeBucket, altitudeColor, fmt } from '../lib/altitude';
import { bearingDeg } from '../lib/geo';

const SRC_TRACK = 'replay-track';
const SRC_POS = 'replay-pos';
const LAYER_TRACK = 'replay-track-line';
const LAYER_POS = 'replay-pos-icon';
/** Komplette Wiedergabe dauert unabhängig von der Flugdauer ~25 s. */
const PLAYBACK_MS = 25_000;

interface TrackReplayProps {
  map: maplibregl.Map;
  track: FlightTrack;
  onClose: () => void;
}

/** Zwischen zwei Messwerten interpolieren, wenn beide vorhanden sind. */
function lerpOpt(a: number | null, b: number | null, f: number): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return a + (b - a) * f;
}

/** Position + Telemetrie auf dem Track bei Fortschritt 0–1 (linear in der Flugzeit). */
function pointAt(points: TrackPoint[], progress: number) {
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const t = first.ts + (last.ts - first.ts) * progress;
  let i = points.findIndex((p) => p.ts > t);
  if (i <= 0) i = progress <= 0 ? 1 : points.length - 1;
  const a = points[i - 1]!;
  const b = points[i]!;
  const span = b.ts - a.ts;
  const f = span > 0 ? (t - a.ts) / span : 0;
  return {
    lon: a.lon + (b.lon - a.lon) * f,
    lat: a.lat + (b.lat - a.lat) * f,
    altFt: lerpOpt(a.alt_ft, b.alt_ft, f),
    gsKt: lerpOpt(a.gs_kt, b.gs_kt, f),
    bearing: bearingDeg(a.lat, a.lon, b.lat, b.lon),
    ts: t,
  };
}

/**
 * Gespeicherten Flug-Track als Polyline mit Höhen-Farbverlauf rendern und
 * animiert abspielen (Polyline-Segmente je Punktpaar, Farbe = Höhe).
 */
export default function TrackReplay({ map, track, onClose }: TrackReplayProps) {
  const points = track.points;
  // Bewusst kein Autoplay: Replay startet pausiert am Anfang des Tracks
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number>(0);
  const progressRef = useRef(0);
  const playingRef = useRef(false);
  const lastFrameRef = useRef<number | null>(null);

  // Layer + Quellen anlegen, auf den Track zoomen
  useEffect(() => {
    if (points.length === 0) return;

    const segments: GeoJSON.Feature[] = [];
    let disposed = false;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1]!;
      const b = points[i]!;
      segments.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [a.lon, a.lat],
            [b.lon, b.lat],
          ],
        },
        properties: { color: altitudeColor(b.alt_ft ?? undefined) },
      });
    }

    const start = pointAt(points, 0);
    function addLayers() {
      if (disposed || map.getSource(SRC_TRACK)) return;
      map.addSource(SRC_TRACK, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: segments },
      });
      map.addLayer({
        id: LAYER_TRACK,
        type: 'line',
        source: SRC_TRACK,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': 2.5, 'line-opacity': 0.9 },
      });

      map.addSource(SRC_POS, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [start.lon, start.lat] },
              properties: {
                bucket: altitudeBucket(start.altFt ?? undefined),
                bearing: start.bearing,
              },
            },
          ],
        },
      });
      map.addLayer({
        id: LAYER_POS,
        type: 'symbol',
        source: SRC_POS,
        layout: {
          'icon-image': ['concat', 'plane-', ['get', 'bucket']],
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-size': 1.0,
        },
      });
    }
    addLayers();
    // Basemap-Wechsel löscht alle Layer — Replay-Layer danach neu aufbauen
    map.on('style.load', addLayers);

    const lats = points.map((p) => p.lat);
    const lons = points.map((p) => p.lon);
    map.fitBounds(
      [
        [Math.min(...lons), Math.min(...lats)],
        [Math.max(...lons), Math.max(...lats)],
      ],
      { padding: 90, duration: 800, maxZoom: 9 },
    );

    return () => {
      disposed = true;
      map.off('style.load', addLayers);
      cancelAnimationFrame(rafRef.current);
      if (map.getLayer(LAYER_POS)) map.removeLayer(LAYER_POS);
      if (map.getLayer(LAYER_TRACK)) map.removeLayer(LAYER_TRACK);
      if (map.getSource(SRC_POS)) map.removeSource(SRC_POS);
      if (map.getSource(SRC_TRACK)) map.removeSource(SRC_TRACK);
    };
  }, [map, points]);

  const renderAt = useCallback(
    (p: number) => {
      if (points.length < 2) return;
      const pos = pointAt(points, p);
      const source = map.getSource<GeoJSONSource>(SRC_POS);
      source?.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
            properties: { bucket: altitudeBucket(pos.altFt ?? undefined), bearing: pos.bearing },
          },
        ],
      });
    },
    [map, points],
  );

  // Animations-Loop
  useEffect(() => {
    if (points.length < 2) return;
    playingRef.current = playing;
    if (!playing) {
      lastFrameRef.current = null;
      return;
    }

    const step = (now: number) => {
      if (!playingRef.current) return;
      if (lastFrameRef.current !== null) {
        const dt = now - lastFrameRef.current;
        let next = progressRef.current + dt / PLAYBACK_MS;
        if (next >= 1) {
          next = 1;
          setPlaying(false);
        }
        progressRef.current = next;
        setProgress(next);
        renderAt(next);
      }
      lastFrameRef.current = now;
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, points, renderAt]);

  const seek = (p: number) => {
    progressRef.current = p;
    setProgress(p);
    renderAt(p);
  };

  const current = points.length >= 2 ? pointAt(points, progress) : null;
  const routeLabel =
    track.flight.origin_icao && track.flight.dest_icao
      ? `${track.flight.origin_icao} → ${track.flight.dest_icao}`
      : (track.flight.callsign ?? track.flight.hex.toUpperCase());

  return (
    <div className="replay-bar">
      <div className="replay-head mono">
        <span className="accent">REPLAY</span> {routeLabel} · {fmt.date(track.flight.started_at)}
      </div>
      {points.length < 2 ? (
        <p className="muted replay-empty">
          Zu wenige Track-Punkte für ein Replay ({points.length}).
        </p>
      ) : (
        <div className="replay-controls">
          <button
            className="replay-btn mono"
            onClick={() => {
              if (progress >= 1) seek(0);
              setPlaying(!playing);
            }}
          >
            {playing ? '❚❚' : '▶'}
          </button>
          <input
            type="range"
            min={0}
            max={1000}
            value={Math.round(progress * 1000)}
            onChange={(e) => {
              setPlaying(false);
              seek(Number(e.target.value) / 1000);
            }}
          />
          <span className="replay-readout mono">
            {current
              ? `${fmt.time(current.ts)} · ${fmt.alt(current.altFt ?? undefined)} · ${fmt.speed(
                  current.gsKt ?? undefined,
                )}`
              : ''}
          </span>
        </div>
      )}
      <button className="close replay-close" onClick={onClose} aria-label="Replay schließen">
        ✕
      </button>
    </div>
  );
}
